// Rapport journalier de l'unité briques & pavés : production par article (et rebut), matériaux, personnel, observations.
// Création par l'équipe ; correction par le responsable (même écran, /briques/rapports/:id/modifier).
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Grid, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SendOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Panel } from '../../components/ui';
import { briquesAPI } from '../../services/api';
import { apiError, dateFr, today } from '../../utils/format';
import { tokens } from '../../theme';

export const MATERIAUX = [
  { nom: 'Ciment 32,5 R', unites: ['sac', 't'] },
  { nom: 'Sable', unites: ['m³', 't', 'brouette'] },
  { nom: 'Gravillon', unites: ['m³', 't', 'brouette'] },
  { nom: 'Poussière de basalte', unites: ['m³', 't', 'brouette'] },
  { nom: 'Adjuvant', unites: ['l', 'kg'] },
  { nom: 'Colorant', unites: ['kg', 'l'] },
  { nom: 'Gasoil (engins)', unites: ['l'] },
];
export const PERSONNEL = ['Conducteur de presse', 'Malaxeur', 'Manœuvre', 'Chef d\'équipe', 'Chauffeur', 'Autre'];
const vide = () => ({ report_date: today(), employee_name: '', employee_firstname: '', observations: '' });

const RapportBriquesForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [familles, setFamilles] = useState({});
  const [f, setF] = useState(vide());
  const [prod, setProd] = useState({}); // article_id -> { quantite, rebut }
  const [mats, setMats] = useState({}); // nom -> { q, unite }
  const [autreMat, setAutreMat] = useState({ nom: '', q: '', unite: '' });
  const [pers, setPers] = useState({});
  const [deja, setDeja] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState('');

  useEffect(() => { briquesAPI.articles().then((r) => { setArticles(r.data.articles); setFamilles(r.data.familles); }).catch((e) => setError(apiError(e))); }, []);
  useEffect(() => {
    if (!id) return;
    briquesAPI.rapport(id).then((r) => {
      const d = r.data;
      setF({ report_date: d.report_date, employee_name: d.employee_name || '', employee_firstname: d.employee_firstname || '', observations: d.observations || '' });
      setProd(Object.fromEntries(d.lignes.map((l) => [l.article_id, { quantite: String(l.quantite || ''), rebut: l.rebut ? String(l.rebut) : '' }])));
      const connus = MATERIAUX.map((m) => m.nom);
      const m = {}; let autre = { nom: '', q: '', unite: '' };
      for (const [k0, v] of Object.entries(d.materiaux || {})) { const k = k0 === 'Ciment' ? 'Ciment 32,5 R' : k0; if (connus.includes(k)) m[k] = { q: v.q, unite: v.unite }; else autre = { nom: k, q: v.q, unite: v.unite }; }
      setMats(m); setAutreMat(autre); setPers(Object.fromEntries(Object.entries(d.personnel || {}).map(([k, v]) => [k, String(v)])));
    }).catch((e) => setError(apiError(e)));
  }, [id]);
  // Un seul rapport par date : on prévient dès le choix de la date
  useEffect(() => {
    if (!f.report_date) return;
    briquesAPI.existe(f.report_date).then((r) => setDeja(r.data.exists && String(r.data.id) !== String(id || '') ? r.data : null)).catch(() => setDeja(null));
  }, [f.report_date, id, ok]);

  const groupes = useMemo(() => Object.keys(familles).map((k) => ({ k, label: familles[k], items: articles.filter((a) => a.famille === k) })).filter((g) => g.items.length), [articles, familles]);
  const setP = (aid, patch) => setProd((p) => ({ ...p, [aid]: { quantite: '', rebut: '', ...p[aid], ...patch } }));
  const total = articles.reduce((s, a) => s + (Number(String(prod[a.id]?.quantite || '').replace(',', '.')) || 0), 0);

  const envoyer = async () => {
    setBusy(true); setError(''); setOk('');
    const materiaux = Object.fromEntries(Object.entries(mats).filter(([, v]) => v.q && String(v.q).trim()).map(([k, v]) => [k, { q: v.q, unite: v.unite || MATERIAUX.find((m) => m.nom === k)?.unites[0] }]));
    if (autreMat.nom.trim() && String(autreMat.q).trim()) materiaux[autreMat.nom.trim()] = { q: autreMat.q, unite: autreMat.unite };
    const body = {
      ...f, materiaux, personnel: Object.fromEntries(Object.entries(pers).filter(([, v]) => Number(v) > 0)),
      lignes: Object.entries(prod).map(([aid, v]) => ({ article_id: Number(aid), quantite: String(v.quantite || '').replace(',', '.'), rebut: String(v.rebut || '').replace(',', '.') })),
    };
    try {
      if (id) { await briquesAPI.modifier(id, body); navigate(`/briques/rapports/${id}`, { state: { info: 'Rapport modifié' } }); return; }
      const r = await briquesAPI.creer(body);
      setOk(`${r.data.message}. Le responsable de l'unité a été prévenu.`);
      setF(vide()); setProd({}); setMats({}); setPers({}); setAutreMat({ nom: '', q: '', unite: '' });
      window.scrollTo(0, 0);
    } catch (e) { setError(apiError(e)); window.scrollTo(0, 0); } finally { setBusy(false); }
  };

  return (
    <Layout title={id ? 'Corriger le rapport briques & pavés' : 'Rapport journalier — Briques & pavés'} plain>
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk('')}>{ok}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {deja && <Alert severity="warning" sx={{ mb: 2 }}>Un rapport existe déjà pour le {dateFr(f.report_date)} (saisi par {deja.saisi_par || '—'}). Choisissez une autre date ou demandez au responsable de le corriger.</Alert>}

      <Panel title="Informations" sx={{ mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField fullWidth required label="Nom" value={f.employee_name} onChange={(e) => setF({ ...f, employee_name: e.target.value })} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth required label="Prénom" value={f.employee_firstname} onChange={(e) => setF({ ...f, employee_firstname: e.target.value })} /></Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth required type="date" label="Date" InputLabelProps={{ shrink: true }} inputProps={{ max: today() }}
              value={f.report_date} onChange={(e) => setF({ ...f, report_date: e.target.value })} />
          </Grid>
        </Grid>
      </Panel>

      <Panel title="Production du jour" flush sx={{ mb: 2 }}
        action={<Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Total produit : <b style={{ fontFamily: tokens.mono }}>{total.toLocaleString('fr-FR')}</b></Typography>}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: { xs: 0, sm: 520 }, '& td, & th': { px: { xs: 1, sm: 2 } } }}>
            <TableHead>
              <TableRow><TableCell>Article</TableCell><TableCell sx={{ width: { xs: 86, sm: 150 } }}>Produit (bon)</TableCell><TableCell sx={{ width: { xs: 76, sm: 150 } }}>Rebut</TableCell><TableCell sx={{ width: 80, display: { xs: 'none', sm: 'table-cell' } }}>Unité</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {groupes.map((g) => (
                <React.Fragment key={g.k}>
                  <TableRow><TableCell colSpan={4} sx={{ bgcolor: '#F7F8FA', fontWeight: 700, fontSize: 13, color: tokens.steel, py: 0.75 }}>{g.label}</TableCell></TableRow>
                  {g.items.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell sx={{ fontWeight: 500 }}>{a.designation}<Box component="span" sx={{ display: { xs: 'block', sm: 'none' }, fontSize: 12, color: 'text.secondary', fontWeight: 400 }}>{a.unite}</Box></TableCell>
                      <TableCell>
                        <TextField variant="standard" fullWidth value={prod[a.id]?.quantite || ''} onChange={(e) => setP(a.id, { quantite: e.target.value })}
                          inputProps={{ inputMode: 'decimal', 'aria-label': `${a.designation} produit`, style: { textAlign: 'right', fontFamily: tokens.mono } }} />
                      </TableCell>
                      <TableCell>
                        <TextField variant="standard" fullWidth value={prod[a.id]?.rebut || ''} onChange={(e) => setP(a.id, { rebut: e.target.value })}
                          inputProps={{ inputMode: 'decimal', 'aria-label': `${a.designation} rebut`, style: { textAlign: 'right', fontFamily: tokens.mono } }} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'table-cell' } }}>{a.unite}</TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Panel title="Matériaux utilisés" sx={{ height: '100%' }}>
            <Grid container spacing={1.5}>
              {MATERIAUX.map((m) => (
                <Grid item xs={12} sm={6} key={m.nom}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField fullWidth size="small" label={m.nom} value={mats[m.nom]?.q || ''} inputProps={{ inputMode: 'decimal' }}
                      onChange={(e) => setMats({ ...mats, [m.nom]: { unite: mats[m.nom]?.unite || m.unites[0], q: e.target.value } })} />
                    <TextField select size="small" sx={{ minWidth: 104 }} value={mats[m.nom]?.unite || m.unites[0]} inputProps={{ 'aria-label': `Unité ${m.nom}` }}
                      onChange={(e) => setMats({ ...mats, [m.nom]: { q: mats[m.nom]?.q || '', unite: e.target.value } })}>
                      {m.unites.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                    </TextField>
                  </Box>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" label="Autre matériau" placeholder="ex. Huile de décoffrage" value={autreMat.nom} onChange={(e) => setAutreMat({ ...autreMat, nom: e.target.value })} sx={{ flex: 2 }} />
                  <TextField size="small" label="Quantité" value={autreMat.q} onChange={(e) => setAutreMat({ ...autreMat, q: e.target.value })} sx={{ flex: 1 }} />
                  <TextField size="small" label="Unité" value={autreMat.unite} onChange={(e) => setAutreMat({ ...autreMat, unite: e.target.value })} sx={{ flex: 1 }} />
                </Box>
              </Grid>
            </Grid>
          </Panel>
        </Grid>
        <Grid item xs={12} md={5}>
          <Panel title="Personnel mobilisé" sx={{ height: '100%' }}>
            <Grid container spacing={1.5}>
              {PERSONNEL.map((p) => (
                <Grid item xs={6} key={p}>
                  <TextField fullWidth size="small" type="number" label={p} value={pers[p] || ''} inputProps={{ min: 0 }} onChange={(e) => setPers({ ...pers, [p]: e.target.value })} />
                </Grid>
              ))}
            </Grid>
          </Panel>
        </Grid>
      </Grid>

      <Panel title="Observations" sx={{ mb: 2 }}>
        <TextField fullWidth multiline minRows={2} placeholder="Pannes, coupures, ruptures de matière, qualité…" value={f.observations}
          onChange={(e) => setF({ ...f, observations: e.target.value })} inputProps={{ 'aria-label': 'Observations', maxLength: 4000 }} />
      </Panel>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        {id && <Button onClick={() => navigate(-1)}>Annuler</Button>}
        <Button variant="contained" startIcon={<SendOutlined />} onClick={envoyer}
          disabled={busy || !!deja || !f.report_date || !f.employee_name.trim() || !f.employee_firstname.trim()}>
          {id ? 'Enregistrer les corrections' : 'Envoyer le rapport'}
        </Button>
      </Box>
    </Layout>
  );
};

export default RapportBriquesForm;
